import i18next from 'i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as Yup from 'yup';
import { LAUNCHDARKLY_TOGGLES } from '@studio/constants/launchdarkly-toggle-constants';
import { CONSTRAINTS, VALID_PASSCODE, VALID_SITE_URL } from '@studio/constants/sites';

type _FormValidationParams = {
  isEdit?: boolean;
};

const useSiteFormValidation = (params: _FormValidationParams = { isEdit: false }) => {
  const { isEdit } = params;
  const { [LAUNCHDARKLY_TOGGLES.PASSCODE_PROTECTED_SITES]: passcodeProtectedSites } = useFlags();

  const urlRequired = () => {
    if (!isEdit) {
      return Yup.string()
        .required(i18next.t('pages:sites.fields.url.required'))
        .matches(VALID_SITE_URL, i18next.t('pages:sites.fields.url.characters'));
    } else {
      return Yup.string();
    }
  };

  const passcodeRule = () => {
    if (!passcodeProtectedSites) {
      return Yup.string();
    }

    return Yup.string().when('isPasscodeProtected', {
      is: true,
      then: (schema) => {
        const rule = i18next.t('pages:sites.fields.passcode.rule', {
          max: CONSTRAINTS.MAX_PASSCODE_LENGTH,
          min: CONSTRAINTS.MIN_PASSCODE_LENGTH,
        });
        return schema
          .required(i18next.t('pages:sites.fields.passcode.required'))
          .min(CONSTRAINTS.MIN_PASSCODE_LENGTH, rule)
          .max(CONSTRAINTS.MAX_PASSCODE_LENGTH, rule)
          .matches(VALID_PASSCODE, rule);
      },
      otherwise: (schema) => schema,
    });
  };

  const passcodeHelpTextRule = () => {
    if (!passcodeProtectedSites) {
      return Yup.string();
    }

    return Yup.string().when('isPasscodeProtected', {
      is: true,
      then: (schema) =>
        schema
          .trim()
          .required(i18next.t('pages:sites.fields.passcodeHelpText.required'))
          .max(
            CONSTRAINTS.MAX_PASSCODE_HELP_TEXT_LENGTH,
            i18next.t('pages:sites.fields.passcodeHelpText.tooLong', {
              count: CONSTRAINTS.MAX_PASSCODE_HELP_TEXT_LENGTH,
            })
          ),
      otherwise: (schema) => schema,
    });
  };

  return Yup.object({
    url: urlRequired(),
    googleAnalyticsId: Yup.string().test(
      'url-format',
      i18next.t('pages:sites.fields.googleAnalyticsId.format'),
      (value) => {
        if (value) {
          const validUrlBasic = /^(G)-[a-zA-Z0-9-]+$/;
          return validUrlBasic.test(value);
        }
        return true;
      }
    ),
    isUrlAvailable: Yup.bool().oneOf([true], i18next.t('pages:sites.fields.url.unavailable')),
    name: Yup.string().required(i18next.t('pages:sites.fields.name.required')),
    passcode: passcodeRule(),
    passcodeHelpText: passcodeHelpTextRule(),
  });
};

export default useSiteFormValidation;
